/** Scale and layout maths for the dumbbell chart. Dependency-free so node-env jest can load it. */

export type Domain = [number, number];

/** ~7px per char at the chart's font size; the cap stops long asset IDs eating the plot. */
const LABEL_WIDTH_PER_CHAR_PX = 7;
const LABEL_COLUMN_PADDING_PX = 20;
const LABEL_COLUMN_MAX_WIDTH_PX = 250;

const DEFAULT_MAX_TICKS = 4;

/** Any closer to an end and the interior tick's label collides with the end's. */
const MIN_INTERIOR_TICK_GAP_FRACTION = 0.2;

/** Below this span the chevron overhangs the connector it sits on. */
const MIN_CARET_SPAN_PERCENT = 4;

/** Rounding iterates until the bounds stop moving; the cap only guards pathological input. */
const MAX_BOUND_ROUNDING_PASSES = 6;
/** Each pass drops to a coarser step; six covers any realistic overshoot. */
const MAX_TICK_THINNING_PASSES = 6;

/** Strips the noise `value / step * step` accumulates, without losing $ magnitudes. */
const stripFloatingPointNoise = (value: number): number =>
  Number.isFinite(value) ? Number(value.toPrecision(12)) : value;

/** Nearest 1, 2 or 5 × 10^k. May round down, so it is not safe for bounds that must contain a value. */
const snapToNearestOneTwoFive = (value: number): number => {
  const exponent = Math.floor(Math.log10(value));
  // in [1, 10), so only the leading magnitude picks the notch
  const normalizedValue = value / Math.pow(10, exponent);

  let snappedValue: number;
  if (normalizedValue < 1.5) snappedValue = 1;
  else if (normalizedValue < 3) snappedValue = 2;
  else if (normalizedValue < 7) snappedValue = 5;
  else snappedValue = 10;

  return stripFloatingPointNoise(snappedValue * Math.pow(10, exponent));
};

/** Round step that fits `maxTicks` labels across the span. */
const tickStepForSpan = (span: number, maxTicks: number): number =>
  snapToNearestOneTwoFive(span / Math.max(1, maxTicks - 1));

/** Next 1/2/5 notch up, for thinning ticks when a step produces too many. */
const nextLargerTickStep = (step: number): number => {
  const exponent = Math.floor(Math.log10(step));
  const normalizedStep = Math.round(step / Math.pow(10, exponent));
  const nextNormalizedStep = normalizedStep < 2 ? 2 : normalizedStep < 5 ? 5 : 10;
  return stripFloatingPointNoise(nextNormalizedStep * Math.pow(10, exponent));
};

const floorToMultipleOf = (value: number, step: number): number =>
  stripFloatingPointNoise(Math.floor(stripFloatingPointNoise(value / step)) * step);

const ceilToMultipleOf = (value: number, step: number): number =>
  stripFloatingPointNoise(Math.ceil(stripFloatingPointNoise(value / step)) * step);

/** Null rather than a sentinel when nothing is measurable, so callers pick their own fallback. */
export const extentOfDumbbells = (points: { start: number; end: number }[]): Domain | null => {
  let min = Infinity;
  let max = -Infinity;

  for (const point of points) {
    for (const value of [point.start, point.end]) {
      if (!Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }

  return Number.isFinite(min) && Number.isFinite(max) ? [min, max] : null;
};

/** A lone asset, or a flat portfolio, has span 0 — every mark would collapse onto the left edge. */
export const widenZeroWidthExtent = (min: number, max: number): Domain => {
  const [lowerBound, upperBound] = min <= max ? [min, max] : [max, min];
  if (upperBound > lowerBound) return [lowerBound, upperBound];
  if (lowerBound === 0) return [0, 1];

  const padding = Math.abs(lowerBound) * 0.05;
  return [stripFloatingPointNoise(lowerBound - padding), stripFloatingPointNoise(lowerBound + padding)];
};

/**
 * Rounds out to 1/2/5 bounds: readable tick labels, and the extreme dots clear of the plot edges.
 * Idempotent — it iterates to a fixed point, so the domain cannot drift when fed back through.
 */
export const expandDomainToRoundBounds = (extent: Domain | null, maxTicks: number = DEFAULT_MAX_TICKS): Domain => {
  if (extent === null) return [0, 1];

  let [lowerBound, upperBound] = widenZeroWidthExtent(extent[0], extent[1]);

  for (let pass = 0; pass < MAX_BOUND_ROUNDING_PASSES; pass++) {
    const step = tickStepForSpan(upperBound - lowerBound, maxTicks);
    if (!Number.isFinite(step) || step <= 0) break;

    const roundedLowerBound = floorToMultipleOf(lowerBound, step);
    const roundedUpperBound = ceilToMultipleOf(upperBound, step);
    if (roundedLowerBound === lowerBound && roundedUpperBound === upperBound) break;

    lowerBound = roundedLowerBound;
    upperBound = roundedUpperBound;
  }

  return [lowerBound, upperBound];
};

/**
 * Both domain ends are always ticks — the renderer's first/last label anchoring assumes it.
 * Interior values land on 1/2/5 multiples, minus any that crowds an end label.
 */
export const ticksForDomain = (domain: Domain, maxTicks: number = DEFAULT_MAX_TICKS): number[] => {
  const [min, max] = domain;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [min];

  const span = max - min;
  const minGapFromEnds = span * MIN_INTERIOR_TICK_GAP_FRACTION;

  const buildTicks = (step: number): number[] => {
    const ticks = [min];
    for (let value = ceilToMultipleOf(min, step); value < max; value = stripFloatingPointNoise(value + step)) {
      if (value - min >= minGapFromEnds && max - value >= minGapFromEnds) ticks.push(value);
    }
    ticks.push(max);
    return ticks;
  };

  let step = tickStepForSpan(span, maxTicks);
  let ticks = buildTicks(step);
  for (let pass = 0; pass < MAX_TICK_THINNING_PASSES && ticks.length > maxTicks; pass++) {
    step = nextLargerTickStep(step);
    ticks = buildTicks(step);
  }

  return ticks;
};

/** Unclamped on purpose: callers need the raw value to spot marks outside a narrowed domain. */
export const percentInDomain = (value: number, domain: Domain): number => {
  const [min, max] = domain;
  const span = max - min;
  if (span === 0 || !Number.isFinite(span)) return 0;
  return ((value - min) / span) * 100;
};

export type DumbbellGeometry =
  /** Both ends past the same edge: nothing is plotted, the renderer substitutes a note. */
  | { kind: "off-left" | "off-right" }
  | {
      kind: "visible";
      barLeft: number;
      barWidth: number;
      /** Null when that end sits outside the window, so no dot is drawn for it. */
      startPercent: number | null;
      endPercent: number | null;
      /** Null when the visible segment is too short to hold the chevron. */
      caretPercent: number | null;
      caretPointsLeft: boolean;
    };

/**
 * Clamps here rather than with `overflow-hidden`: dots are centred on their value, so clipping
 * would slice in half one that legitimately sits on a domain bound.
 */
export const projectDumbbell = (
  start: number,
  end: number,
  domain: Domain,
  { minCaretSpanPercent = MIN_CARET_SPAN_PERCENT } = {}
): DumbbellGeometry => {
  const startPercent = percentInDomain(start, domain);
  const endPercent = percentInDomain(end, domain);
  const lowPercent = Math.min(startPercent, endPercent);
  const highPercent = Math.max(startPercent, endPercent);

  if (highPercent < 0) return { kind: "off-left" };
  if (lowPercent > 100) return { kind: "off-right" };

  const clampToWindow = (percent: number) => Math.min(100, Math.max(0, percent));
  const percentIfInside = (percent: number) => (percent < 0 || percent > 100 ? null : percent);

  const barLeft = clampToWindow(lowPercent);
  const barWidth = clampToWindow(highPercent) - barLeft;

  return {
    kind: "visible",
    barLeft,
    barWidth,
    startPercent: percentIfInside(startPercent),
    endPercent: percentIfInside(endPercent),
    // midpoint of the visible (clipped) segment
    caretPercent: barWidth >= minCaretSpanPercent ? barLeft + barWidth / 2 : null,
    // direction is a fact about the data, so it reads the true values, not the clamped ones
    caretPointsLeft: endPercent < startPercent,
  };
};

/** Which ends the axis range clipped, for the tooltip to annotate. */
export const offScaleEnds = (geometry: DumbbellGeometry): { start: boolean; end: boolean } =>
  geometry.kind === "visible"
    ? { start: geometry.startPercent === null, end: geometry.endPercent === null }
    : { start: true, end: true };

/** Orders, clamps and widens a user-entered range so the axis can never collapse to nothing. */
export const normaliseManualRange = (range: Domain, bounds: Domain, minSpan: number): Domain => {
  const [boundsMin, boundsMax] = bounds;
  const clampToBounds = (value: number) => Math.min(Math.max(value, boundsMin), boundsMax);

  const [lower, upper] = range[0] <= range[1] ? range : [range[1], range[0]];
  let min = clampToBounds(lower);
  let max = clampToBounds(upper);
  if (max - min >= minSpan) return [min, max];

  max = Math.min(min + minSpan, boundsMax);
  min = Math.max(max - minSpan, boundsMin);
  return [min, max];
};

/** Needs the unfiltered names: measured over the rendered subset it shifts the plot as you type. */
export const computeLabelColumnWidth = (
  names: string[],
  { pxPerChar = LABEL_WIDTH_PER_CHAR_PX, padding = LABEL_COLUMN_PADDING_PX, maxWidth = LABEL_COLUMN_MAX_WIDTH_PX } = {}
): number => {
  let longestNameLength = 0;
  for (const name of names) {
    if (name.length > longestNameLength) longestNameLength = name.length;
  }
  return Math.min(longestNameLength * pxPerChar + padding, maxWidth);
};
